import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useWebSocket } from '@/contexts/WebSocketContext';
import api from '@/lib/api';
import { Share2, TrendingUp, UserPlus, Zap, Radio } from 'lucide-react';
import logger from '@/lib/logger';

const EVENT_CONFIG = {
  lead_shared: { icon: Share2, color: 'bg-blue-100 text-blue-600', label: 'Shared' },
  group_share: { icon: Share2, color: 'bg-violet-100 text-violet-600', label: 'Group Share' },
  lead_assigned: { icon: Zap, color: 'bg-amber-100 text-amber-600', label: 'Assigned' },
  lead_converted: { icon: TrendingUp, color: 'bg-emerald-100 text-emerald-600', label: 'Converted' },
  member_joined: { icon: UserPlus, color: 'bg-sky-100 text-sky-600', label: 'New Member' },
};

function timeAgo(timestamp) {
  if (!timestamp) return '';
  const diff = (Date.now() - new Date(timestamp).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function TeamActivityFeed() {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newIds, setNewIds] = useState(new Set());
  const { addListener } = useWebSocket();

  useEffect(() => {
    api.getActivityFeed()
      .then(r => setFeed(r.data))
      .catch(err => logger.error(err))
      .finally(() => setLoading(false));
  }, []);

  // Listen for real-time WS events and prepend to feed
  useEffect(() => {
    if (!addListener) return;
    const unsub = addListener('any', (event, data) => {
      let newItem = null;
      if (event === 'lead_shared') {
        newItem = { type: 'lead_shared', user_name: data?.shared_by || 'Someone', details: `Shared '${data?.lead_name || 'a lead'}'`, lead_id: data?.lead_id || '', timestamp: new Date().toISOString() };
      } else if (event === 'lead_assigned') {
        newItem = { type: 'lead_assigned', user_name: 'System', details: `'${data?.lead_name || 'a lead'}' auto-assigned via '${data?.rule_name || 'rule'}'`, lead_id: data?.lead_id || '', timestamp: new Date().toISOString() };
      }
      if (newItem) {
        const id = `${newItem.type}-${newItem.timestamp}`;
        setNewIds(prev => new Set([...prev, id]));
        setFeed(prev => [newItem, ...prev].slice(0, 25));
        setTimeout(() => setNewIds(prev => { const next = new Set(prev); next.delete(id); return next; }), 3000);
      }
    });
    return unsub;
  }, [addListener]);

  if (loading) {
    return (
      <div className="border border-slate-200 rounded-sm bg-white">
        <div className="p-4 border-b border-slate-200 flex items-center gap-2">
          <Radio className="w-4 h-4 text-[#0047AB]" />
          <h2 className="font-heading font-semibold text-slate-900 text-sm">Team Activity</h2>
        </div>
        <div className="flex items-center justify-center py-12"><div className="animate-spin w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full" /></div>
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-sm bg-white" data-testid="team-activity-feed">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Radio className="w-4 h-4 text-[#0047AB]" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          </div>
          <h2 className="font-heading font-semibold text-slate-900 text-sm">Team Activity</h2>
        </div>
        <span className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-medium">Live</span>
      </div>
      <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
        {feed.length === 0 ? (
          <div className="text-center py-10">
            <Radio className="w-8 h-8 text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No team activity yet</p>
            <p className="text-xs text-slate-300 mt-1">Share leads and invite members to see activity here</p>
          </div>
        ) : feed.map((item, i) => {
          const config = EVENT_CONFIG[item.type] || EVENT_CONFIG.lead_shared;
          const Icon = config.icon;
          const id = `${item.type}-${item.timestamp}`;
          const isNew = newIds.has(id);
          return (
            <div
              key={`${item.type}-${item.timestamp}-${i}`}
              className={`flex items-start gap-3 p-3 transition-all duration-500 ${isNew ? 'bg-blue-50/60 animate-slideIn' : 'hover:bg-slate-50'}`}
              data-testid={`feed-item-${i}`}
            >
              <div className={`w-7 h-7 rounded-sm flex items-center justify-center shrink-0 ${config.color}`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-700 leading-snug">
                  <span className="font-medium text-slate-900">{item.user_name}</span>{' '}
                  {item.type === 'member_joined'
                    ? 'joined the team'
                    : item.type === 'lead_converted'
                    ? item.details
                    : item.details}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-slate-400">{timeAgo(item.timestamp)}</span>
                  <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-sm ${config.color}`}>{config.label}</span>
                  {item.lead_id && (
                    <Link to={`/leads/${item.lead_id}`} className="text-[10px] text-[#0047AB] hover:underline">View Lead</Link>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
